import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  AccountService,
  ActionService,
  ShareService,
} from '@arkius/shared-api';
import { ModalController } from '@ionic/angular';
import { Subject, take } from 'rxjs';
import { ThankYouModalComponent } from '../thank-you-modal/thank-you-modal.component';
import { environment } from '@shared-envs';

interface Member {
  userId: string;
  nickname: string;
  icon: string;
}

@Component({
  selector: 'ark-lib-arkius-share-modal',
  templateUrl: './arkius-share-modal.component.html',
  styleUrls: ['./arkius-share-modal.component.css'],
})
export class ArkiusShareModalComponent implements OnInit {
  @Input() id!: number;
  @Input() type!: string;
  @Input() socialName!: string;
  messageValue: string = '';
  searchText: string = '';
  searchQuery = new Subject<string>();
  accountLists!: { followersList: Member[]; followingList: Member[] };
  selectedMembers: Member[] = [];
  originalFollowersList: Member[] = [];
  originalFollowingList: Member[] = [];
  shareForm: FormGroup;

  constructor(
    private accountService: AccountService,
    private actionService: ActionService,
    private modalCtrl: ModalController,
    private fb: FormBuilder,
    private shareService: ShareService,
  ) {
    this.shareForm = this.fb.group({
      messageValue: '',
    });
  }

  ngOnInit() {
    this.getAccDetails();
  }

  getAccDetails() {
    this.accountService.apiV5AccountProfileGet().subscribe((data) => {
      this.accountLists = data;
      this.originalFollowersList = [...data.followersList];
      this.originalFollowingList = [...data.followingList];
    });
  }

  onSearchInputChange(event: any) {
    const query = event.target.value;
    this.searchQuery.next(query);

    if (!query) {
      this.accountService.apiV5AccountProfileGet().subscribe((response) => {
        this.accountLists.followersList = response.followersList;
        this.accountLists.followingList = response.followingList;
      });
    } else {
      this.accountService
        .apiV5AccountProfileGet(query)
        .subscribe((response) => {
          this.accountLists.followersList = response.followersList;
          this.accountLists.followingList = response.followingList;
        });
    }
  }

  selectMember(member: Member) {
    const memberInSelected = this.selectedMembers.find(
      (m) => m.userId === member.userId,
    );
    if (!memberInSelected) {
      this.selectedMembers.push(member);
      this.removeFromLists(member);
    }
  }

  removeSelectedMember(member: Member) {
    this.selectedMembers = this.selectedMembers.filter(
      (m) => m.userId !== member.userId,
    );
    if (this.wasInOriginalFollowers(member)) {
      this.accountLists.followersList.push(member);
    }
    if (this.wasInOriginalFollowing(member)) {
      this.accountLists.followingList.push(member);
    }
  }

  removeFromLists(member: Member) {
    this.accountLists.followersList = this.accountLists.followersList.filter(
      (m) => m.userId !== member.userId,
    );
    this.accountLists.followingList = this.accountLists.followingList.filter(
      (m) => m.userId !== member.userId,
    );
  }

  wasInOriginalFollowers(member: Member): boolean {
    return this.originalFollowersList.some((m) => m.userId === member.userId);
  }

  wasInOriginalFollowing(member: Member): boolean {
    return this.originalFollowingList.some((m) => m.userId === member.userId);
  }

  closeModal() {
    this.modalCtrl.dismiss();
  }

  share() {
    if (this.selectedMembers.length <= 0) return;
    const messageValue = this.shareForm.get('messageValue')?.value;

    const shareData = {
      EntityType: this.type,
      EntityId: this.id,
      SocialName: this.socialName,
    };

    const postData = {
      EntityType: this.type,
      Message: messageValue,
      EntityId: this.id,
      UserIds: this.selectedMembers.map((member) => Number(member.userId)),
    };

    this.actionService
      .apiV5ActionSocialSharePost(postData)
      .pipe(take(1))
      .subscribe({
        next: async (data) => {
          this.modalCtrl.dismiss({ shared: true }, 'confirm');

          const thankYouModal = await this.modalCtrl.create({
            component: ThankYouModalComponent,
          });

          const modalElement = thankYouModal.shadowRoot?.querySelector(
            '.modal-wrapper',
          ) as HTMLElement;
          if (modalElement) {
            modalElement.style.width = '90%';
            modalElement.style.borderRadius = '8px';
          }

          await thankYouModal.present();

          this.shareService
            .apiV5SharePost(shareData)
            .pipe(take(1))
            .subscribe({
              next: (response) => {
                environment.log('Share action recorded:', response);
              },
            });

          setTimeout(() => {
            thankYouModal.dismiss();
          }, 5000);
        },
        error: (error) => {
          console.error('Error sharing:', error);
        },
      });
  }
}
